import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ChevronRight, Network, Search } from "lucide-react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { BottomSheet } from "@/components/app/bottom-sheet";
import { MindmapView } from "@/components/mindmaps/mindmap-view";
import { EXAM_LABEL, SUBJECT_LABEL } from "@/lib/pyq/analytics";
import { EXAM_SUBJECTS } from "@/lib/mindmaps/types";
import {
  countBranches,
  mindmapGroups,
  getMindmaps,
  mindmapSubjects,
  searchMindmaps,
} from "@/lib/mindmaps/selectors";
import type { ExamId, Mindmap, SubjectId } from "@/lib/mindmaps/types";
import { useEntitlement } from "@/hooks/use-entitlement";
import { useUpgrade } from "@/components/billing/premium-lock";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/mindmaps")({
  head: () => ({
    ...seo({
      title: "Chapter Mindmaps for JEE Main & NEET | The Quantum Minds",
      description:
        "Browse chapter mindmaps by exam and subject — Physics, Chemistry, Mathematics and Biology — for fast JEE Main and NEET revision.",
      path: "/mindmaps",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Mindmaps", path: "/mindmaps" },
      ]),
    ],
  }),
  component: MindmapsPage,
});

const CARD =
  "rounded-3xl border border-white/10 bg-card/70 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl";
const PILL =
  "rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all active:scale-95";

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            PILL,
            value === o.id
              ? "border-primary/40 bg-primary/15 text-primary"
              : "border-white/10 bg-card/60 text-muted-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MindmapsPage() {
  const { entitlement } = useEntitlement();
  const { openUpgrade } = useUpgrade();
  const isPremiumUser = entitlement?.paid_active === true;
  const [exam, setExam] = useState<ExamId>("jee");
  const [subject, setSubject] = useState<SubjectId | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<Mindmap | null>(null);

  const openMindmap = (map: Mindmap) => {
    // Premium check runs before the mindmap content is ever opened.
    if (!isPremiumUser) {
      openUpgrade(
        "Premium Mindmaps — unlock chapter-wise mindmaps and revise smarter. Upgrade to Quantum Minds Premium to access this mindmap.",
      );
      return;
    }
    setActive(map);
  };


  const withData = mindmapSubjects(exam);
  const subjects = withData.length ? withData : EXAM_SUBJECTS[exam];
  const currentSubject = subject && subjects.includes(subject) ? subject : (subjects[0] ?? null);

  const list = useMemo(
    () => (currentSubject ? getMindmaps(exam, currentSubject) : []),
    [exam, currentSubject],
  );
  const groups = useMemo(() => mindmapGroups(list), [list]);
  const [group, setGroup] = useState<string | null>(null);
  const currentGroup = group && groups.includes(group) ? group : (groups[0] ?? null);
  const visible = useMemo(
    () => searchMindmaps(list, query).filter((m) => (currentGroup ? m.group === currentGroup : true)),
    [list, query, currentGroup],
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-6">
      <header className="flex items-center gap-3">
        <span className="grid size-11 place-items-center rounded-2xl bg-gradient-to-br from-fuchsia-400/25 to-purple-500/10 text-fuchsia-500 dark:text-fuchsia-300">
          <Network className="size-5" />
        </span>
        <div>
          <h1 className="text-[22px] font-black tracking-tight">Mindmaps</h1>
          <p className="text-[12.5px] text-muted-foreground">
            Chapter mindmaps for JEE Main and NEET revision.
          </p>
        </div>
        <ShareButton className="ml-auto" />
      </header>


      <div className="mt-5 space-y-3">
        <Segmented
          options={[
            { id: "jee" as ExamId, label: `${EXAM_LABEL.jee} Main` },
            { id: "neet" as ExamId, label: EXAM_LABEL.neet },
          ]}
          value={exam}
          onChange={(id) => {
            setExam(id);
            setSubject(null);
          }}
        />
        {currentSubject ? (
          <Segmented
            options={subjects.map((id) => ({ id, label: SUBJECT_LABEL[id] }))}
            value={currentSubject}
            onChange={(id) => {
              setSubject(id);
              setGroup(null);
            }}
          />
        ) : null}
        {currentGroup && groups.length > 1 ? (
          <Segmented
            options={groups.map((g) => ({ id: g, label: g }))}
            value={currentGroup}
            onChange={setGroup}
          />
        ) : null}

        <label className={cn(CARD, "flex items-center gap-2 px-4 py-3")}>
          <Search className="size-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chapters"
            className="w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground"
          />
        </label>
      </div>

      {visible.length === 0 ? (
        <div className={cn(CARD, "mt-6 p-6 text-center")}>
          <p className="text-[14px] font-bold">
            {list.length === 0 ? "Mindmaps coming soon" : "No chapters match your search"}
          </p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            {list.length === 0
              ? `${currentSubject ? SUBJECT_LABEL[currentSubject] : "Subject"} mindmaps will appear here as soon as they are added.`
              : "Try a different chapter name."}
          </p>
        </div>
      ) : (
        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
          {visible.map((map, i) => (
            <motion.button
              key={map.id}
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.2) }}
              onClick={() => openMindmap(map)}
              className={cn(CARD, "flex w-full items-center gap-3 p-4 text-left active:scale-[0.99]")}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-bold">{map.chapter}</p>
                <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                  {[map.group, `${countBranches(map)} branches`].join(" • ")}
                </p>
              </div>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </motion.button>
          ))}
        </div>
      )}

      <BottomSheet
        open={active !== null}
        onClose={() => setActive(null)}
        title={active?.chapter ?? "Mindmap"}
        subtitle={
          active
            ? [EXAM_LABEL[exam], currentSubject ? SUBJECT_LABEL[currentSubject] : null]
                .filter(Boolean)
                .join(" • ")
            : undefined
        }
        tall
      >
        {active ? <MindmapView map={active} /> : null}
      </BottomSheet>
    </div>
  );
}
